import { X } from 'lucide-react';
import { Fragment, useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { initials, lifeSpan, displayName } from '../../model/people';
import type { Id, Person } from '../../model/types';
import { closeDialog, showPerson, useApp } from '../store';

const AVATAR_COLORS = ['#7c6cf0', '#0f766e', '#c2410c', '#be185d', '#2563eb', '#65a30d', '#a16207', '#0e7490', '#9333ea', '#b45309', '#4d7c0f', '#1d4ed8'];

export function avatarColor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

export function Avatar({ person, size }: { person: Person; size?: 'sm' | 'lg' | 'xl' }) {
  return (
    <span className={`avatar${size ? ' ' + size : ''}`} style={{ background: avatarColor(person.id) }} aria-hidden>
      {person.photo ? <img src={person.photo} alt="" draggable={false} /> : initials(person)}
    </span>
  );
}

/** Relationship words in a sentence, each emphasized: "best friend and chosen sister". */
export function JoinedTerms({ terms, className }: { terms: string[]; className?: string }) {
  return (
    <>
      {terms.map((t, i) => (
        <Fragment key={t}>
          {i > 0 && (i < terms.length - 1 ? ', ' : terms.length > 2 ? ', and ' : ' and ')}
          {className ? <span className={className}>{t}</span> : <b>{t}</b>}
        </Fragment>
      ))}
    </>
  );
}

/** A person as a small pill that opens their details. */
export function PersonLink({ id }: { id: Id }) {
  const p = useApp((s) => s.tree?.people[id]);
  if (!p) return null;
  return (
    <button className="person-link" data-person-id={id} onClick={() => showPerson(id)} title="Show details">
      <Avatar person={p} size="sm" />
      {displayName(p)}
    </button>
  );
}

export function PersonRow({
  person,
  meta,
  onClick,
  actions,
}: {
  person: Person;
  meta?: ReactNode;
  onClick?: () => void;
  actions?: ReactNode;
}) {
  const span = lifeSpan(person);
  return (
    <div
      className="person-row"
      data-person-id={person.id}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <Avatar person={person} size="sm" />
      <div className="info">
        <div className="name">{displayName(person)}</div>
        <div className="meta">{[meta, span, person.pronouns].filter(Boolean).map((x, i) => <span key={i}>{i > 0 ? ' · ' : ''}{x}</span>)}</div>
      </div>
      {actions && (
        <div className="row-actions" onClick={(e) => e.stopPropagation()}>
          {actions}
        </div>
      )}
    </div>
  );
}

export function Modal({
  title,
  children,
  footer,
  wide,
  className,
  onClose = closeDialog,
  labelledBy,
  ariaLabel,
}: {
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  className?: string;
  onClose?: () => void;
  labelledBy?: string;
  ariaLabel?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    // Respect a child's autoFocus (React focuses it before this effect runs); otherwise focus
    // the first text field, else the dialog itself so Escape and Tab work.
    if (!ref.current?.contains(document.activeElement)) {
      const el = ref.current?.querySelector<HTMLElement>('input:not([type=checkbox]):not([type=radio]):not([type=file]), textarea') ?? ref.current;
      el?.focus();
    }
    return () => prev?.focus?.();
  }, []);
  return (
    <div
      className="overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        className={`modal${wide ? ' wide' : ''}${className ? ' ' + className : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel ?? (typeof title === 'string' ? title : undefined)}
        aria-labelledby={labelledBy}
        tabIndex={-1}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            onClose();
          } else if (e.key === 'Tab' && ref.current?.contains(e.target as Node)) {
            // Keep Tab inside the dialog: past the last control is the first, and back again.
            // (Popups opened from it live in portals and handle their own keys.)
            const items = [...ref.current.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter(
              (el) => !el.hasAttribute('disabled') && el.offsetParent !== null,
            );
            if (!items.length) return;
            const first = items[0];
            const last = items[items.length - 1];
            if (e.shiftKey && (e.target === first || e.target === ref.current)) {
              e.preventDefault();
              last.focus();
            } else if (!e.shiftKey && e.target === last) {
              e.preventDefault();
              first.focus();
            }
          }
        }}
      >
        {title !== undefined && (
          <div className="modal-head">
            <h2>{title}</h2>
            <button className="btn icon ghost" onClick={onClose} aria-label="Close">
              <X size={18} />
            </button>
          </div>
        )}
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

export function Field({ label, hint, warn, children, htmlFor }: { label: ReactNode; hint?: ReactNode; warn?: boolean; children: ReactNode; htmlFor?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const autoId = useId();
  // Link the label to the first form control inside, so screen readers announce it
  // and clicking the label focuses the control, without every caller passing ids.
  useLayoutEffect(() => {
    if (htmlFor) return;
    const root = ref.current;
    const labelEl = root?.querySelector(':scope > label') as HTMLLabelElement | null;
    const control = root?.querySelector<HTMLElement>('input:not([type=checkbox]):not([type=radio]), select, textarea');
    if (!labelEl || !control) return;
    if (!control.id) control.id = autoId;
    labelEl.htmlFor = control.id;
  });
  return (
    <div className="field" ref={ref}>
      <label htmlFor={htmlFor}>{label}</label>
      {children}
      {hint && <div className={`hint${warn ? ' warn' : ''}`}>{hint}</div>}
    </div>
  );
}

export function Select<T extends string>({
  value,
  onChange,
  options,
  id,
  ariaLabel,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[] | Record<T, string>;
  id?: string;
  ariaLabel?: string;
}) {
  const opts = Array.isArray(options) ? options : (Object.entries(options) as [T, string][]).map(([value, label]) => ({ value, label }));
  return (
    <select id={id} aria-label={ariaLabel} className="select" value={value} onChange={(e) => onChange(e.target.value as T)}>
      {opts.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function Segmented<T extends string>({ value, onChange, options, ariaLabel }: { value: T; onChange: (v: T) => void; options: { value: T; label: ReactNode; title?: string }[]; ariaLabel: string }) {
  return (
    <div className="seg" role="group" aria-label={ariaLabel}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={value === o.value} title={o.title} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Stepper({ value, onChange, min, max, label }: { value: number; onChange: (v: number) => void; min: number; max: number; label: string }) {
  return (
    <span className="stepper" role="group" aria-label={label}>
      <button type="button" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min} aria-label={`Fewer ${label.toLowerCase()}`}>
        −
      </button>
      <span aria-live="polite">{value}</span>
      <button type="button" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max} aria-label={`More ${label.toLowerCase()}`}>
        +
      </button>
    </span>
  );
}

export function Empty({ icon, title, children }: { icon?: ReactNode; title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <div>
        {icon}
        <h3>{title}</h3>
        {children && <div style={{ marginTop: 6 }}>{children}</div>}
      </div>
    </div>
  );
}

/**
 * Where a dropdown list goes: under its field, or above it when there's more room there. It's
 * `position: fixed`, for a list drawn in a portal, so no dialog or scroll area clips it (NOTES.md).
 * `size` is anything that changes the list's height; `minWidth` widens lists under narrow fields.
 */
export function useDropdownPlace(anchor: RefObject<HTMLElement>, shown: boolean, size: unknown, minWidth = 0): CSSProperties | null {
  const [place, setPlace] = useState<CSSProperties | null>(null);
  useLayoutEffect(() => {
    if (!shown) {
      setPlace(null);
      return;
    }
    const update = () => {
      const r = anchor.current?.getBoundingClientRect();
      if (!r) return;
      const below = window.innerHeight - r.bottom;
      const up = below < 220 && r.top > below;
      const width = Math.min(Math.max(r.width, minWidth), window.innerWidth - 16);
      const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
      setPlace({ position: 'fixed', left, width, ...(up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }), maxHeight: Math.min(300, (up ? r.top : below) - 12) });
    };
    update();
    window.addEventListener('resize', update);
    document.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      document.removeEventListener('scroll', update, true);
    };
  }, [anchor, shown, size, minWidth]);
  return shown ? place : null;
}
